import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getMappedOpportunityData, updateContactWithFormData, getContactFiles } from '../api/hubspotApi';
import { getOpportunityFields, getFieldMappings } from '../api/fieldMappings';
import { getSubmissionHistory } from '../api/lenderApi';
import { getSettings } from '../api/settings';
import { getSecondaryContactByProperty } from '../api/hubspot';
import { useNotifications } from '../context/NotificationContext';
import { LenderSubmission, FileUploads } from '../components/forms';
import SideNavigation from '../components/SideNavigation';
import { ValidatedInput } from '../components/forms';
import { formatDateToYYYYMMDD } from '../utils/lenderUtils';
import { 
  validateSSN, 
  validatePhone, 
  validateEmail, 
  validateCurrency, 
  validateZip, 
  validateEIN,
  validateDateOfBirth,
  validateBusinessStartDate,
  validateSicCode,
  validateOwnershipPercentage,
  validateStreetAddress,
  validateCity,
  US_STATES,
  getStateCodeFromText,
  validateState
} from '../utils';
import '../styles/ContactDetail.css';
import { FaArrowLeft, FaCheck, FaExclamationTriangle, FaSync, FaUserPlus, FaUserMinus } from 'react-icons/fa';
import ValidationSummary from '../components/ValidationSummary';
import { toast } from 'react-toastify';

// --- Client-Specific Configuration ---
// TODO: Replace this hardcoded flag with a proper configuration mechanism (e.g., env variable, settings API)
const IS_CRESTMONT_CLIENT = true; 
// -----------------------------------

// Custom styles for required asterisk
const requiredStyle = {
  color: 'var(--danger)',
  marginLeft: '2px'
};

// Helper function to check if a field is a state field
const isStateField = (fieldName) => {
  const stateFields = [
    'State',
    'ContactState',
    'Contact2State',
    'ContactStateOfIncorporation',
    'Contact2StateOfIncorporation',
    'BusinessState' // Added BusinessState to be handled as a state dropdown
  ];
  return stateFields.includes(fieldName);
};

// Helper function to format field date for display
const formatDateForDisplay = (date) => {
  if (!date) return '';
  // Try to parse the date
  try {
    const dateObj = new Date(date);
    // Check if valid date
    if (isNaN(dateObj.getTime())) return date;
    // Return formatted date
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return date;
  }
};

// Helper function to convert string date to Date object
const stringToDate = (dateString) => {
  if (!dateString) return null;
  try {
    // Fix timezone issues by using a specific construction method
    // Format expected is YYYY-MM-DD
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
      
      // Create date with explicit year, month (0-based), day and set time to noon
      // Setting noon (12:00:00) avoids timezone shifts that can occur at midnight
      const date = new Date(year, month - 1, day, 12, 0, 0);
      console.log(`stringToDate: Converted ${dateString} to ${date.toISOString()}`);
      return date;
    }
    
    // For other formats, try using Date constructor but still set time to noon
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      // Reset time to noon on the same day to avoid timezone issues
      const correctedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
      console.log(`stringToDate: Converted ${dateString} to ${correctedDate.toISOString()}`);
      return correctedDate;
    }
    return null;
  } catch (e) {
    console.error('Error in stringToDate:', e);
    return null;
  }
};

// Helper function to convert Date object to ISO string format yyyy-MM-dd
const dateToString = (date) => {
  // If no date or invalid date, return empty string
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }
  
  // Format to yyyy-MM-dd using UTC values to avoid timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');  // month is 0-based in JS
  const day = String(date.getDate()).padStart(2, '0');
  
  const result = `${year}-${month}-${day}`;
  console.log(`dateToString: Converted ${date.toISOString()} to ${result}`);
  return result;
};

// Helper function to parse combined address string
const parseCombinedAddress = (addressString) => {
  if (!addressString || typeof addressString !== 'string' || !addressString.includes(',')) {
    // Return default structure if not a parsable string
    return { street: addressString || '', city: '', state: '', zip: '' };
  }

  const parts = addressString.split(',').map(part => part.trim());
  let street = '', city = '', state = '', zip = '';

  if (parts.length === 4) {
    // Format: Street, City, State, Zip
    street = parts[0];
    city = parts[1];
    state = getStateCodeFromText(parts[2]); // Convert state name to code
    zip = parts[3];
    console.log(`Parsed 4-part address: S=${street}, C=${city}, ST=${state}, Z=${zip}`);
  } else if (parts.length === 5) {
    // Format: Street, Suite/Apt, City, State, Zip
    street = `${parts[0]}, ${parts[1]}`; // Combine first two parts for street
    city = parts[2];
    state = getStateCodeFromText(parts[3]); // Convert state name to code
    zip = parts[4];
    console.log(`Parsed 5-part address: S=${street}, C=${city}, ST=${state}, Z=${zip}`);
  } else {
    // Unexpected format, assign whole string to street and log warning
    console.warn(`Unexpected address format (${parts.length} parts): "${addressString}". Assigning full string to street.`);
    street = addressString;
  }

  return { street, city, state, zip };
};

// Determine which validation to use based on field name
const getValidatorForField = (field) => {
  const fieldName = field.name.toLowerCase();
  const fieldLabel = field.label.toLowerCase();
  
  // Check for state fields first
  if (isStateField(field.name)) {
    return validateState;
  }
  
  if (fieldName.includes('ssn') || fieldName.includes('social') || fieldLabel.includes('social security')) {
    return validateSSN;
  } else if (fieldName.includes('phone') || fieldName.includes('mobile') || fieldName.includes('cell')) {
    return validatePhone;
  } else if (fieldName.includes('email')) {
    return validateEmail;
  } else if (fieldName.includes('zip') || fieldName.includes('postal')) {
    return validateZip;
  } else if (fieldName.includes('ein') || fieldName.includes('tax_id') || fieldLabel.includes('tax id')) {
    return validateEIN;
  } else if (
    fieldName.includes('revenue') || 
    fieldName.includes('amount') || 
    fieldName.includes('balance') || 
    fieldName.includes('price') || 
    fieldName.includes('income')
  ) {
    return validateCurrency;
  } else if (
    fieldName.includes('dob') || 
    fieldName.includes('dateofbirth') || 
    fieldName.includes('date_of_birth') || 
    fieldLabel.includes('date of birth') || 
    (fieldName.includes('birth') && fieldName.includes('date'))
  ) {
    return validateDateOfBirth;
  } else if (
    fieldName.includes('sic') || 
    fieldLabel.includes('sic code') ||
    fieldLabel.includes('industry code')
  ) {
    return validateSicCode;
  } else if (
    fieldName === 'businessstartdate' ||
    (fieldName.includes('business') && fieldName.includes('start') && fieldName.includes('date'))
  ) {
    return validateBusinessStartDate;
  } else if (
    fieldName.includes('ownership') || 
    fieldLabel.includes('ownership percentage')
  ) {
    return validateOwnershipPercentage;
  } else if (
    fieldName.includes('street') || 
    fieldName.includes('address1') || 
    fieldName.includes('address_1') ||
    fieldName.includes('address_line_1') ||
    fieldName.includes('addressline1') ||
    (fieldLabel.includes('address') && (
      fieldLabel.includes('street') || 
      fieldLabel.includes('line 1') || 
      fieldLabel.includes('line1')
    ))
  ) {
    return validateStreetAddress;
  } else if (
    fieldName.includes('city') ||
    fieldLabel.includes('city')
  ) {
    return validateCity;
  }
  
  return null;
};

const ContactDetail = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [businessData, setBusinessData] = useState({});
  const [businessFields, setBusinessFields] = useState([]);
  const [categories, setCategories] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [canSubmitToLenders, setCanSubmitToLenders] = useState(false);
  const [syncWithCRM, setSyncWithCRM] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  
  // New state for the multi-step form
  const [activeSection, setActiveSection] = useState(0);
  const [sectionTransitioning, setSectionTransitioning] = useState(false);
  const [completionStats, setCompletionStats] = useState({});
  
  // Add state for file uploads
  const [bankFiles, setBankFiles] = useState([]);
  const [appFiles, setAppFiles] = useState([]);
  const [fileUploadsComplete, setFileUploadsComplete] = useState(false);
  const [fileUploadStats, setFileUploadStats] = useState({ completed: 0, total: 2 });
  
  // Pre-loaded HubSpot files (fetched in background on page load)
  const [preloadedHubspotFiles, setPreloadedHubspotFiles] = useState([]);
  const [hubspotFilesLoading, setHubspotFilesLoading] = useState(false);

  // New state for secondary contact
  const [hasSecondaryContact, setHasSecondaryContact] = useState(false);
  
  const [secondaryContactHandling, setSecondaryContactHandling] = useState('manual');
  const [secondaryContactProperty, setSecondaryContactProperty] = useState('');
  
  const { success, error: notifyError, warning } = useNotifications();
  
  // Add state for tracking auto-loaded secondary contact
  const [autoLoadedSecondaryContact, setAutoLoadedSecondaryContact] = useState(false);
  
  // New state for submission history
  const [submissionHistory, setSubmissionHistory] = useState({});
  
  // Fetch contact and business data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log("Starting contact data fetch process");
        
        // Fetch business fields
        const fields = await getOpportunityFields();
        setBusinessFields(fields);
        const mappedCategories = fields.map(field => getMappedCategory(field.category));
        const uniqueCategories = [...new Set(mappedCategories), 'File Uploads', 'Submit to Lender'];
        setCategories(uniqueCategories);
        
        let settingsData = null;
        try {
          console.log("Fetching settings...");
          settingsData = await getSettings();
          setSecondaryContactHandling(settingsData.secondaryContactHandling || 'manual');
          setSecondaryContactProperty(settingsData.secondaryContactProperty || '');
        } catch (settingsError) {
          console.error('Error fetching settings:', settingsError);
          // Continue without settings, default to manual
          setSecondaryContactHandling('manual');
          setSecondaryContactProperty('');
        }

        console.log(`Fetching contact data for ID: ${contactId}`);
        const data = await getMappedOpportunityData(contactId);
        let parsedOpportunityData = { ...(data.opportunityData || {}) };

        // --- Client-Specific Parsing Logic (Primary Data) --- 
        if (IS_CRESTMONT_CLIENT) {
          console.log("Applying Crestmont-specific parsing...");
          
          // 1. BusinessStartDate Parsing (MM/YYYY -> YYYY-MM-01)
          const rawStartDate = parsedOpportunityData.BusinessStartDate;
          if (rawStartDate && typeof rawStartDate === 'string') {
            // Direct matching and string manipulation to avoid timezone issues
            const monthYearRegex = /^(\d{1,2})\/(\d{4})$/;
            const match = rawStartDate.match(monthYearRegex);
            
            if (match) {
              const month = match[1].padStart(2, '0'); // Ensure month is 2 digits
              const year = match[2];
              // Direct string assignment - avoid Date object creation to prevent timezone issues
              const exactDateString = `${year}-${month}-01`;
              console.log(`Parsed BusinessStartDate using direct string method: "${rawStartDate}" -> "${exactDateString}"`);
              parsedOpportunityData.BusinessStartDate = exactDateString;
            } else {
              // If not MM/YYYY format, try standard formatting (but log extensively)
              const formattedDate = formatDateToYYYYMMDD(rawStartDate);
              console.log(`Attempted to parse date with formatDateToYYYYMMDD: "${rawStartDate}" -> "${formattedDate}"`);
              
              if (formattedDate !== rawStartDate) {
                parsedOpportunityData.BusinessStartDate = formattedDate;
              }
            }
          } else {
            console.log(`BusinessStartDate not found or not a string.`);
          }

          // 2. Business Address Parsing
          if (parsedOpportunityData.BusinessStreet && parsedOpportunityData.BusinessStreet.includes(',')) {
            const parsedAddress = parseCombinedAddress(parsedOpportunityData.BusinessStreet);
            parsedOpportunityData = { ...parsedOpportunityData, ...{
              BusinessStreet: parsedAddress.street,
              BusinessCity: parsedAddress.city,
              BusinessState: parsedAddress.state,
              BusinessZipCode: parsedAddress.zip
            }};
          }

          // 3. Contact Address Parsing
          if (parsedOpportunityData.ContactStreet && parsedOpportunityData.ContactStreet.includes(',')) {
            const parsedAddress = parseCombinedAddress(parsedOpportunityData.ContactStreet);
            parsedOpportunityData = { ...parsedOpportunityData, ...{
              ContactStreet: parsedAddress.street,
              ContactCity: parsedAddress.city,
              ContactState: parsedAddress.state,
              ContactZipCode: parsedAddress.zip
            }};
          }
        }
        // --- End Client-Specific Parsing Logic (Primary Data) ---

        setContactData(data.contactData);
        // Temporarily set data, secondary contact logic might overwrite/merge
        setBusinessData(parsedOpportunityData); 

        // --- Secondary Contact Handling --- 
        // First, check if we're in HubSpot mode and if so, prioritize the designated property check
        if (settingsData && settingsData.secondaryContactHandling === 'hubspot' && settingsData.secondaryContactProperty && contactId) {
          // Check if the designated HubSpot property has a value in the contact data
          const propertyName = settingsData.secondaryContactProperty;
          const propertyValue = data.contactData?.properties?.[propertyName];
          
          // Define what "has value" means: not null, not undefined, and not an empty string after trimming
          const propertyHasValue = propertyValue !== null && 
                                  propertyValue !== undefined && 
                                  String(propertyValue).trim() !== '';
          
          console.log(`Checking secondary contact HubSpot property ${propertyName}:`, propertyValue, `Has value: ${propertyHasValue}`);
          
          // Only show secondary contact section if the property has a value
          if (propertyHasValue) {
            console.log('Secondary contact property has a value. Checking if secondary contact data exists...');
            
            // Try to fetch secondary contact data if available
            try {
              console.log(`Attempting to fetch secondary contact data using property ${propertyName}`);
              const secondaryContactData = await getSecondaryContactByProperty(contactId, propertyName);
              
              // Only show secondary contact section if we actually get data back from the server
              if (secondaryContactData) {
                console.log('Secondary contact data fetched successfully. Showing secondary contact section.');
                setHasSecondaryContact(true);
                
                // Get the field mappings to map HubSpot properties to opportunity fields
                const fieldMappings = await getFieldMappings();
                
                // Create secondary contact data object
                const secondaryData = {};
                let hasUpdatedAnyField = false;
                
                // Get all contact fields that should have secondary counterparts
                const contactFields = businessFields.filter(field => 
                  field.category === 'Primary Contact' || field.category === 'Contact Address'
                );
                
                // Map each field using our stored mappings
                contactFields.forEach(primaryField => {
                  const secondaryFieldName = `Contact2${primaryField.name.replace('Contact', '')}`;
                  
                  // Find the mapping for this field
                  const mapping = fieldMappings.find(m => m.opportunityField === secondaryFieldName);
                  
                  if (mapping && mapping.hubspotField && secondaryContactData.properties[mapping.hubspotField] !== undefined) {
                    // Get the value and ensure it's a string
                    let value = secondaryContactData.properties[mapping.hubspotField];
                    
                    // Convert value to string based on its type
                    if (value === null || value === undefined) {
                      value = '';
                    } else if (typeof value === 'object') {
                      // If it's an object with a value property (common in HubSpot)
                      value = value.value ? String(value.value) : '';
                    } else {
                      value = String(value);
                    }
                    
                    // Special handling for date fields
                    if (primaryField.name.toLowerCase().includes('dateofbirth') || 
                        (primaryField.name.toLowerCase().includes('date') && 
                         primaryField.name.toLowerCase().includes('birth'))) {
                      value = formatDateToYYYYMMDD(value);
                    }
                    
                    // Special handling for numeric fields
                    if (primaryField.name.toLowerCase().includes('ownership') || 
                        primaryField.name.toLowerCase().includes('revenue') || 
                        primaryField.name.toLowerCase().includes('amount')) {
                      value = value.replace(/[^0-9.]/g, '');
                    }
                    
                    console.log(`Mapping ${secondaryFieldName} using ${mapping.hubspotField}:`, value);
                    secondaryData[secondaryFieldName] = value;
                    hasUpdatedAnyField = true;
                  }
                });
                
                // Apply data if we found any fields to map
                if (hasUpdatedAnyField) {
                  console.log('Applying secondary contact data:', secondaryData);
                  parsedOpportunityData = { ...parsedOpportunityData, ...secondaryData };
                  setAutoLoadedSecondaryContact(true);
                } else {
                  console.log('No secondary contact fields could be mapped, but section will be shown.');
                }
                
                // Ensure parsing applies AFTER merging secondary data
                if (IS_CRESTMONT_CLIENT && parsedOpportunityData.Contact2Street && parsedOpportunityData.Contact2Street.includes(',')) {
                    const parsedAddress = parseCombinedAddress(parsedOpportunityData.Contact2Street);
                    parsedOpportunityData = { ...parsedOpportunityData, ...{
                      Contact2Street: parsedAddress.street,
                      Contact2City: parsedAddress.city,
                      Contact2State: parsedAddress.state,
                      Contact2ZipCode: parsedAddress.zip
                    }};
                }
              } else {
                console.log('No secondary contact data found despite property having a value. NOT showing secondary contact section.');
                setHasSecondaryContact(false);
                setAutoLoadedSecondaryContact(false);
                
                // Clean up any existing Contact2* fields if they exist
                const updatedData = { ...parsedOpportunityData };
                let removedFields = false;
                Object.keys(updatedData).forEach(key => {
                  if (key.startsWith('Contact2')) {
                    delete updatedData[key];
                    removedFields = true;
                  }
                });
                
                if (removedFields) {
                  console.log('Removed existing Contact2* fields to ensure secondary contact section remains hidden');
                  parsedOpportunityData = updatedData;
                }
              }
      } catch (error) {
              console.error('Error fetching secondary contact data:', error);
              // If there's an error, don't show the secondary contact section
              console.log('Error occurred while fetching secondary contact data. NOT showing secondary contact section.');
              setHasSecondaryContact(false);
              setAutoLoadedSecondaryContact(false);
              
              // Clean up any existing Contact2* fields if they exist
              const updatedData = { ...parsedOpportunityData };
              let removedFields = false;
              Object.keys(updatedData).forEach(key => {
                if (key.startsWith('Contact2')) {
                  delete updatedData[key];
                  removedFields = true;
                }
              });
              
              if (removedFields) {
                console.log('Removed existing Contact2* fields to ensure secondary contact section remains hidden');
                parsedOpportunityData = updatedData;
              }
            }
          } else {
            // Property exists but has no value - don't show secondary contact section
            console.log('Secondary contact property is empty. Secondary contact section will NOT be loaded.');
            setHasSecondaryContact(false);
            setAutoLoadedSecondaryContact(false);
            
            // Clean up any existing Contact2* fields if they exist
            // This prevents any fields from keeping the section visible
            const updatedData = { ...parsedOpportunityData };
            let removedFields = false;
            Object.keys(updatedData).forEach(key => {
              if (key.startsWith('Contact2')) {
                delete updatedData[key];
                removedFields = true;
              }
            });
            
            if (removedFields) {
              console.log('Removed existing Contact2* fields to ensure secondary contact section remains hidden');
              parsedOpportunityData = updatedData;
            }
          }
        } 
        // Now, for non-HubSpot mode or if HubSpot mode is not properly configured:
        else {
          // If we're in HubSpot mode with a property configured but missing contactId,
          // we should NOT show secondary contact section
          if (settingsData && settingsData.secondaryContactHandling === 'hubspot' && settingsData.secondaryContactProperty) {
            // We're in HubSpot mode with a configured property, but we're in the else block
            // which means contactId is missing
            // In this case, we should NOT show secondary contact section regardless of existing Contact2* fields
            console.log('In HubSpot mode but contact ID missing. Secondary contact section will NOT be loaded.');
            setHasSecondaryContact(false);
            
            // Clean up any existing Contact2* fields
            const updatedData = { ...parsedOpportunityData };
            let removedFields = false;
            Object.keys(updatedData).forEach(key => {
              if (key.startsWith('Contact2')) {
                delete updatedData[key];
                removedFields = true;
              }
            });
            
            if (removedFields) {
              console.log('Removed existing Contact2* fields in HubSpot mode to ensure secondary contact section remains hidden');
              parsedOpportunityData = updatedData;
            }
          } 
          // Only check for existing secondary contact data if we're in manual mode
          else if (settingsData && settingsData.secondaryContactHandling === 'manual') {
            // Check if secondary contact data exists in the parsed opportunity data
            const hasExistingSecondary = Object.keys(parsedOpportunityData).some(key => 
              key.startsWith('Contact2') && parsedOpportunityData[key]
            );
            
            if (hasExistingSecondary) {
              console.log('Secondary contact data already exists in local data (manual mode).');
              setHasSecondaryContact(true);
              // Ensure parsing applies to existing secondary address
              if (IS_CRESTMONT_CLIENT && parsedOpportunityData.Contact2Street && parsedOpportunityData.Contact2Street.includes(',')) {
                 const parsed = parseCombinedAddress(parsedOpportunityData.Contact2Street);
                 parsedOpportunityData = { ...parsedOpportunityData, Contact2Street: parsed.street, Contact2City: parsed.city, Contact2State: parsed.state, Contact2ZipCode: parsed.zip };
              }
            } else {
              console.log('No existing secondary contact data found in manual mode.');
              setHasSecondaryContact(false);
            }
          } else {
            // No valid configuration, default to not showing secondary contact
            console.log('No valid secondary contact configuration found. Secondary contact section will NOT be loaded.');
            setHasSecondaryContact(false);
            
            // Clean up any existing Contact2* fields
            const updatedData = { ...parsedOpportunityData };
            let removedFields = false;
            Object.keys(updatedData).forEach(key => {
              if (key.startsWith('Contact2')) {
                delete updatedData[key];
                removedFields = true;
              }
            });
            
            if (removedFields) {
              console.log('Removed existing Contact2* fields due to no valid configuration');
              parsedOpportunityData = updatedData;
            }
          }
        }
        // --- End Secondary Contact Handling ---

        // Set final business data state AFTER all parsing and potential secondary contact merge
        console.log("Setting final business data state:", parsedOpportunityData);
        
        // --- Phone Number Preprocessing ---
        const preprocessPhoneNumber = (phone) => {
          if (!phone || typeof phone !== 'string') return phone;
          let cleanedPhone = phone.trim();
          if (cleanedPhone.startsWith('+1')) {
            cleanedPhone = cleanedPhone.substring(2);
          } else if (cleanedPhone.startsWith('1') && cleanedPhone.length > 10) {
            cleanedPhone = cleanedPhone.substring(1);
          } else if (cleanedPhone.startsWith('+')) {
            cleanedPhone = cleanedPhone.substring(1);
          }
          return cleanedPhone;
        };
        
        if (parsedOpportunityData.ContactCellPhone) {
          const originalPhone = parsedOpportunityData.ContactCellPhone;
          parsedOpportunityData.ContactCellPhone = preprocessPhoneNumber(originalPhone);
          if (originalPhone !== parsedOpportunityData.ContactCellPhone) {
            console.log(`Preprocessed ContactCellPhone: "${originalPhone}" -> "${parsedOpportunityData.ContactCellPhone}"`);
          }
        }
        if (parsedOpportunityData.Contact2CellPhone) {
          const originalPhone = parsedOpportunityData.Contact2CellPhone;
          parsedOpportunityData.Contact2CellPhone = preprocessPhoneNumber(originalPhone);
          if (originalPhone !== parsedOpportunityData.Contact2CellPhone) {
            console.log(`Preprocessed Contact2CellPhone: "${originalPhone}" -> "${parsedOpportunityData.Contact2CellPhone}"`);
          }
        }
        // --- End Phone Number Preprocessing ---
        
        // Round AnnualRevenue to nearest whole number if it's a decimal
        if (parsedOpportunityData.AnnualRevenue) {
          const originalValue = parsedOpportunityData.AnnualRevenue;
          // Remove all non-numeric characters except decimal point for parsing
          const cleanValue = String(originalValue).replace(/[^0-9.-]/g, '');
          const numericValue = parseFloat(cleanValue);
          
          if (!isNaN(numericValue)) {
            // Always convert to a whole number by removing decimal portion entirely
            const wholeNumber = Math.round(numericValue);
            // Store as a string with no formatting (no commas, no decimal places)
            const formattedWholeNumber = wholeNumber.toString();
            
            if (originalValue !== formattedWholeNumber) {
              console.log(`Converted AnnualRevenue from "${originalValue}" to whole number "${formattedWholeNumber}"`);
              parsedOpportunityData.AnnualRevenue = formattedWholeNumber;
            }
          }
        }
        
        // Ensure SIC Code is always set to 0000
        parsedOpportunityData.SICCode = "0000";
        console.log("Enforced SICCode to 0000");
        
        // Auto-populate DBA with BusinessLegalName if DBA is empty
        if (parsedOpportunityData.BusinessLegalName && 
            (!parsedOpportunityData.DBA || parsedOpportunityData.DBA.trim() === '')) {
          console.log(`Auto-populating DBA with BusinessLegalName: ${parsedOpportunityData.BusinessLegalName}`);
          parsedOpportunityData.DBA = parsedOpportunityData.BusinessLegalName;
        }
        
        // Auto-populate AverageBankBalance if empty, missing, or zero
        const currentAvgBalance = parsedOpportunityData.AverageBankBalance;
        if (currentAvgBalance === null || 
            currentAvgBalance === undefined || 
            String(currentAvgBalance).trim() === '' || 
            parseFloat(String(currentAvgBalance).replace(/[^0-9.-]/g, '')) === 0) {
          console.log(`Auto-populating AverageBankBalance with '20000' as it was empty or zero. Original value: '${currentAvgBalance}'`);
          parsedOpportunityData.AverageBankBalance = "20000";
        }
        
        // Auto-populate RequestedAmount to 15% of AnnualRevenue if empty, missing, or zero
        const currentRequestedAmount = parsedOpportunityData.RequestedAmount;
        if (currentRequestedAmount === null || 
            currentRequestedAmount === undefined || 
            String(currentRequestedAmount).trim() === '' || 
            parseFloat(String(currentRequestedAmount).replace(/[^0-9.-]/g, '')) === 0) {
          
          // Get AnnualRevenue and calculate 15%
          const annualRevenue = parsedOpportunityData.AnnualRevenue;
          if (annualRevenue && parseFloat(String(annualRevenue).replace(/[^0-9.-]/g, '')) > 0) {
            // Clean the annual revenue value and calculate 15%
            const cleanedRevenue = parseFloat(String(annualRevenue).replace(/[^0-9.-]/g, ''));
            let calculatedAmount = Math.round(cleanedRevenue * 0.15);
            
            // Cap at 500000 if it exceeds the maximum loan amount
            if (calculatedAmount > 500000) {
              console.log(`Capping RequestedAmount at 500000 (was ${calculatedAmount})`);
              calculatedAmount = 500000;
            }
            
            console.log(`Auto-populating RequestedAmount with '${calculatedAmount}' (15% of Annual Revenue ${cleanedRevenue}). Original value: '${currentRequestedAmount}'`);
            parsedOpportunityData.RequestedAmount = calculatedAmount.toString();
          } else {
            // If no valid AnnualRevenue, default to a reasonable value
            console.log(`Auto-populating RequestedAmount with '50000' as AnnualRevenue was not available. Original value: '${currentRequestedAmount}'`);
            parsedOpportunityData.RequestedAmount = "50000";
          }
        }
        
        setBusinessData(parsedOpportunityData); 

      } catch (error) {
        // Main data fetch error handling
        console.error('Error fetching contact data or settings:', error);
        if (error.response && error.response.status === 404) {
          setError('Contact not found. Please check the contact ID and try again.');
        } else {
          setError('Failed to load contact data. Please try again later.');
        }
        // Remove problematic parsing attempt from this catch block
      } finally {
        setLoading(false);
      }
    };
    
    if (contactId) {
      fetchData();
    } else {
      setError('Contact ID is required');
      setLoading(false);
    }
  }, [contactId]); // Keep original dependencies
  
  // Pre-load HubSpot files in background on page load (with rate limiting and date filtering)
  useEffect(() => {
    const preloadHubspotFiles = async () => {
      if (!contactId) return;
      
      try {
        setHubspotFilesLoading(true);
        console.log('[ContactDetail] Pre-loading HubSpot files in background...');
        
        // Fetch files from last 30 days (optimized with Search API)
        const response = await getContactFiles(contactId);
        const files = response.files || [];
        
        setPreloadedHubspotFiles(files);
        console.log(`[ContactDetail] ✓ Pre-loaded ${files.length} HubSpot files`);
      } catch (error) {
        console.error('[ContactDetail] Error pre-loading HubSpot files:', error);
        // Don't block the UI, just log the error
        setPreloadedHubspotFiles([]);
      } finally {
        setHubspotFilesLoading(false);
      }
    };
    
    // Start pre-loading after a small delay to let main page content load first
    const timeoutId = setTimeout(() => {
      preloadHubspotFiles();
    }, 500); // 500ms delay to prioritize main page content
    
    return () => clearTimeout(timeoutId);
  }, [contactId]);
  
  // Validate initial data once business data and fields are loaded
  useEffect(() => {
    if (!loading && businessFields.length > 0 && Object.keys(businessData).length > 0) {
      console.log("Running initial validation check on loaded data");
      
      // Force validation of all fields on initial load
      const isValid = validateForm();
      updateCompletionStats();
      
      // Log any validation errors found in initial data
      if (!isValid) {
        console.warn("Validation errors found in initial data load:", validationErrors);
        // Show validation errors immediately for initial data problems
        setShowValidationErrors(true);
      }
    }
  }, [loading, businessFields, businessData]);
  
  // Update component when validation errors change
  useEffect(() => {
    // Update canSubmitToLenders based on validation errors AND file uploads
    setCanSubmitToLenders(validationErrors.length === 0 && fileUploadsComplete);
    
    // Also update completion stats
    if (businessFields.length > 0) {
      updateCompletionStats();
    }
  }, [validationErrors, businessFields, fileUploadsComplete]);
  
  // Check if file upload requirements are met
  useEffect(() => {
    const bankFilesUploaded = bankFiles.length > 0 ? 1 : 0;
    const appFilesUploaded = appFiles.length > 0 ? 1 : 0;
    const totalUploaded = bankFilesUploaded + appFilesUploaded;
    
    setFileUploadStats({
      completed: totalUploaded,
      total: 2
    });
    
    // Only require bank statements, app files are optional
    setFileUploadsComplete(bankFiles.length > 0);
  }, [bankFiles, appFiles]);
  
  // Function to update completion statistics
  const updateCompletionStats = () => {
    if (businessFields.length === 0) return;
    
    const stats = {};
    categories.forEach(category => {
      const categoryFields = businessFields.filter(field => 
        getMappedCategory(field.category) === category
      );
      
      let completedFields = 0;
      let totalFields = categoryFields.length;
      
      // Count completed primary fields
      completedFields = categoryFields.filter(field => {
        const value = businessData[field.name];
        
        // First check if field is not empty
        const isEmpty = value === undefined || value === null || 
            (typeof value !== 'number' && value.toString().trim() === '');
        if (isEmpty) {
          return false;
        }
        
        // Then check field-specific validation if available
        const validator = getValidatorForField(field);
        if (validator && validator.validate) {
          const validationResult = validator.validate(value);
          return validationResult.isValid;
        }
        
        // If no validator, consider it valid if not empty
        return true;
      }).length;
      
      // Add secondary contact fields to the calculation for Contact Information category
      if (hasSecondaryContact && category === 'Contact Information') {
        // Filter for contact fields (Primary Contact and Contact Address)
        const contactFields = categoryFields.filter(field => 
          field.category === 'Primary Contact' || field.category === 'Contact Address'
        );
        
        // Add to total fields count
        totalFields += contactFields.length;
        
        // Count completed secondary fields
        const completedSecondaryFields = contactFields.filter(field => {
          const secondaryFieldName = `Contact2${field.name.replace('Contact', '')}`;
          const value = businessData[secondaryFieldName];
          
          // First check if field is not empty
          const isEmpty = value === undefined || value === null || 
              (typeof value !== 'number' && value.toString().trim() === '');
          if (isEmpty) {
            return false;
          }
          
          // Then check field-specific validation if available
          const validator = getValidatorForField(field);
          if (validator && validator.validate) {
            const validationResult = validator.validate(value);
            return validationResult.isValid;
          }
          
          // If no validator, consider it valid if not empty
          return true;
        }).length;
        
        // Add to completed fields count
        completedFields += completedSecondaryFields;
      }
      
      stats[category] = {
        completed: completedFields,
        total: totalFields
      };
    });
    
    console.log('Updated completion stats:', stats);
    setCompletionStats(stats);
  };
  
  // Helper function to remap categories without changing field properties
  const getMappedCategory = (originalCategory) => {
    // Map primary contact and contact address categories to Contact Information
    if (originalCategory === 'Primary Contact' || originalCategory === 'Contact Address') {
      return 'Contact Information';
    }
    
    // Map account information and business address to Business Information
    if (originalCategory === 'Account Information' || originalCategory === 'Business Address') {
      return 'Business Information';
    }
    
    // Keep other categories as is
    return originalCategory;
  };
  
  // Function to validate the form
  const validateForm = () => {
    console.log("--- Starting validateForm ---");
    let errors = [];
    const primaryOwnershipField = 'ContactOwnershipPercentage';
    const secondaryOwnershipField = 'Contact2OwnershipPercentage';
    let combinedOwnershipError = false; // Flag for combined error
    let combinedErrorMessage = '';

    // 1. Perform combined ownership validation FIRST if secondary contact exists
    if (hasSecondaryContact) {
      console.log("Step 1: Checking combined ownership FIRST...");
      const primaryValue = businessData[primaryOwnershipField];
      const secondaryValue = businessData[secondaryOwnershipField];
      console.log(`Values - Primary: ${primaryValue}, Secondary: ${secondaryValue}`);

      const combinedValidation = validateOwnershipPercentage.validateCombined(
        primaryValue,
        secondaryValue
      );
      console.log("Combined validation result:", combinedValidation);

      if (!combinedValidation.isValid) {
        console.log("Combined validation FAILED. Setting combined error flag.");
        combinedOwnershipError = true;
        combinedErrorMessage = combinedValidation.message;
        // Add the error for both fields immediately
        errors.push({ field: primaryOwnershipField, message: combinedErrorMessage });
        errors.push({ field: secondaryOwnershipField, message: combinedErrorMessage });
        console.log(`Added combined error message to both ownership fields: "${combinedErrorMessage}"`);
      }
    }

    // 2. Perform individual validation for all fields
    console.log("Step 2: Performing individual validation...");
    businessFields.forEach(field => {
      // Check if we should skip validation for this field due to combined error
      if (combinedOwnershipError && 
          (field.name === primaryOwnershipField || field.name === secondaryOwnershipField)) {
        console.log(`Skipping individual validation for ${field.name} due to combined error.`);
        return; // Skip individual checks if combined error already exists for this field
      }
      
      const value = businessData[field.name];
      const isEmpty = value === undefined || value === null || 
        (typeof value !== 'number' && String(value).trim() === '');
      
      if (field.name.startsWith('Contact2') && !hasSecondaryContact) return;
      if (field.optional) return;

      // Required check
      if (isEmpty) {
        // Add required error only if it doesn't already exist 
        if (!errors.some(e => e.field === field.name)) {
          const errorMsg = `${field.label} is required`;
          errors.push({ field: field.name, message: errorMsg });
          console.log(`Required field error for ${field.name}: "${errorMsg}"`);
        }
        return; // Don't run further validation if empty
      }

      // Field-specific validator (handles individual range checks etc.)
        const validator = getValidatorForField(field);
        if (validator && validator.validate) {
        const result = validator.validate(value);
        if (!result.isValid) {
          // Add error only if it doesn't already exist
          if (!errors.some(e => e.field === field.name)) {
            errors.push({ field: field.name, message: result.message });
            console.log(`Validation error for ${field.name}: "${result.message}"`);
          }
        }
      }
    });
    
    // Also validate secondary contact fields if they exist
    if (hasSecondaryContact) {
      // Get all the contact fields (excluding ownership percentage which was already checked)
      console.log("Step A3: Validating secondary contact fields...");
      const contactFields = businessFields.filter(field => 
        (field.category === 'Primary Contact' || field.category === 'Contact Address') &&
        field.name !== primaryOwnershipField
      );
      
      contactFields.forEach(primaryField => {
        // Skip ownership field since it was handled separately above
        if (primaryField.name === primaryOwnershipField) return;
        
        // Create secondary field name (Contact2Name, Contact2Email, etc.)
        const secondaryFieldName = `Contact2${primaryField.name.replace('Contact', '')}`;
        const secondaryFieldLabel = primaryField.label.replace('Primary', 'Secondary');
        
        // Skip optional fields
        if (primaryField.optional) return;
        
        const value = businessData[secondaryFieldName];
        const isEmpty = value === undefined || value === null || 
          (typeof value !== 'number' && String(value).trim() === '');
        
        // Required check
        if (isEmpty) {
          // Add required error only if it doesn't already exist
          if (!errors.some(e => e.field === secondaryFieldName)) {
            const errorMsg = `${secondaryFieldLabel} is required`;
            errors.push({ field: secondaryFieldName, message: errorMsg });
            console.log(`Required field error for ${secondaryFieldName}: "${errorMsg}"`);
          }
          return; // Don't run further validation if empty
        }
        
        // Field-specific validator (handles email, phone, etc.)
        const validator = getValidatorForField(primaryField);
        if (validator && validator.validate) {
          const result = validator.validate(value);
          if (!result.isValid) {
            // Add error only if it doesn't already exist
            if (!errors.some(e => e.field === secondaryFieldName)) {
              errors.push({ field: secondaryFieldName, message: result.message });
              console.log(`Validation error for ${secondaryFieldName}: "${result.message}"`);
            }
        }
      }
    });
    }
    
    console.log("Final validation errors:", errors);
    setValidationErrors(errors);
    
    return errors.length === 0;
  };
  
  // Effect to handle validation whenever businessData changes
  useEffect(() => {
    // Only run validation after initial load and when businessFields are available
    if (!loading && businessFields.length > 0) { 
      console.log("Business data or secondary contact status changed, running validation.");
      validateForm();
      // Also update completion stats after validation
      updateCompletionStats(); 
    }
  // Depend on businessData, hasSecondaryContact, loading state, and businessFields availability
  }, [businessData, hasSecondaryContact, loading, businessFields]);
  
  // Handle form input changes
  const handleInputChange = (field, value) => {
    let fieldName, fieldValue;
    
    // Extract field name and value
    if (typeof field === 'object' && field.target) {
      fieldName = field.target.name;
      fieldValue = field.target.value;
    } else {
      fieldName = field;
      fieldValue = value;
    }
    
    // Preprocess value for state fields
    fieldValue = preprocessStateValue(fieldName, fieldValue);
    
    // Format date values to yyyy-mm-dd
    if (fieldValue && 
        (fieldName.toLowerCase().includes('dateofbirth') || 
         (fieldName.toLowerCase().includes('date') && fieldName.toLowerCase().includes('birth')))) {
      // Use the formatDateToYYYYMMDD function to standardize date format
      const formattedDate = formatDateToYYYYMMDD(fieldValue);
      console.log(`Formatted date ${fieldName} from ${fieldValue} to ${formattedDate}`);
      fieldValue = formattedDate;
    }
    
    // Check if this is an ownership percentage field
    const isOwnershipField = fieldName === 'ContactOwnershipPercentage' || fieldName === 'Contact2OwnershipPercentage';
    
    // Check if this is one of our number fields
    const isNumberField = fieldName === 'RequestedAmount' || fieldName === 'AverageBankBalance';
    
    // For ownership percentage fields, enforce numeric formatting
    if (isOwnershipField && fieldValue) {
      // Remove any non-numeric characters except decimal point
      fieldValue = fieldValue.replace(/[^\d.]/g, '');
      
      // Ensure only one decimal point
      const parts = fieldValue.split('.');
      if (parts.length > 2) {
        fieldValue = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Convert to number and cap at 100 if needed
      const numValue = parseFloat(fieldValue);
      if (!isNaN(numValue) && numValue > 100) {
        fieldValue = '100';
      }
    }
    
    // For RequestedAmount and AverageBankBalance, ensure valid number formatting
    if (isNumberField && fieldValue) {
      // Handle empty string
      if (fieldValue === '') {
        fieldValue = '';
      } else {
        // Ensure it's a valid number
        const numValue = parseFloat(fieldValue);
        if (!isNaN(numValue)) {
          // Keep it as a string for consistency with other fields
          fieldValue = numValue.toString();
        }
      }
    }
    
    // Update business data - validation will be triggered by useEffect
    setBusinessData(prevData => ({
      ...prevData,
      [fieldName]: fieldValue
    }));
    
    // Reset sync status when form data changes
    if (syncStatus) {
      setSyncStatus(null);
    }
  };
  
  // Effect to handle validation whenever businessData changes
  useEffect(() => {
    if (!loading) { // Only run validation after initial load
      console.log("Business data changed, running validation.");
      validateForm();
    }
  }, [businessData, hasSecondaryContact]); // Re-run if businessData or secondary contact status changes
  
  // Effect to handle sync with CRM when checkbox is checked
  useEffect(() => {
    if (syncWithCRM && !syncLoading && !syncStatus) {
      handleSyncWithCRM();
    }
  }, [syncWithCRM, syncLoading, syncStatus]);
  
  // Handle sync with CRM
  const handleSyncWithCRM = async () => {
    // Validate form first
    const isValid = validateForm();
    if (!isValid) {
      setShowValidationErrors(true);
      notifyError(
        'Please fix all validation errors before syncing with HubSpot.', 
        'Validation Error'
      );
      return;
    }
    
    try {
      setSyncLoading(true);
      setSyncStatus(null);
      setSyncWithCRM(true); // Set the sync flag to true when manually triggered
      
      // Normalize data before sending to API
      const normalizedData = { ...businessData };
      
      // Apply field-specific normalization
      businessFields.forEach(field => {
        const validator = getValidatorForField(field);
        if (validator && validator.normalize && normalizedData[field.name] !== undefined) {
          normalizedData[field.name] = validator.normalize(normalizedData[field.name]);
        }
      });
      
      console.log('Syncing form data with HubSpot CRM:', normalizedData);
      
      // Update HubSpot contact with form data
      const result = await updateContactWithFormData(contactId, normalizedData);
      
      console.log('Sync result:', result);
      
      if (result.success) {
        setSyncStatus({
          type: 'success',
          message: 'Successfully synced data with HubSpot CRM.'
        });
        
        // Update contact data with the updated contact
        if (result.updatedContact) {
          setContactData(result.updatedContact);
        }
        
        // Send success notification
        success(
          'Contact data successfully synced with HubSpot CRM.',
          'Sync Successful'
        );
      } else {
        setSyncStatus({
          type: 'warning',
          message: result.message || 'No changes were made to the HubSpot contact.'
        });
        
        // Send warning notification
        warning(
          result.message || 'No changes were made to the HubSpot contact.',
          'Sync Warning'
        );
      }
    } catch (error) {
      console.error('Error syncing with CRM:', error);
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to sync with HubSpot CRM.';
      
      setSyncStatus({
        type: 'error',
        message: errorMessage
      });
      
      // Send error notification
      notifyError(
        errorMessage,
        'Sync Failed'
      );
      
      setSyncWithCRM(false); // Reset the sync flag on error
    } finally {
      setSyncLoading(false);
    }
  };
  
  // Helper function to check if a field is numerical
  const isNumericalField = (fieldName) => {
    return fieldName.includes('Revenue') || 
           fieldName.includes('Amount') || 
           fieldName.includes('Balance') || 
           fieldName.includes('Percentage');
  };
  
  // Preprocess field value for state fields
  const preprocessStateValue = (fieldName, value) => {
    if (isStateField(fieldName) && value) {
      // Try to match the text to a state code
      const stateCode = getStateCodeFromText(value);
      return stateCode || '';
    }
    return value;
  };

  // Handle initial data loading and state code conversion
  useEffect(() => {
    if (!loading && Object.keys(businessData).length > 0) {
      // Convert any state fields from text to state codes
      const updatedData = { ...businessData };
      let hasChanges = false;

      Object.keys(updatedData).forEach(fieldName => {
        // Handle state fields
        if (isStateField(fieldName) && updatedData[fieldName]) {
          const stateCode = getStateCodeFromText(updatedData[fieldName]);
          if (stateCode && stateCode !== updatedData[fieldName]) {
            updatedData[fieldName] = stateCode;
            hasChanges = true;
          }
        }
        
        // Format date of birth fields to yyyy-mm-dd
        if (updatedData[fieldName] && 
            (fieldName.toLowerCase().includes('dateofbirth') || 
             (fieldName.toLowerCase().includes('date') && fieldName.toLowerCase().includes('birth')))) {
          const formattedDate = formatDateToYYYYMMDD(updatedData[fieldName]);
          if (formattedDate !== updatedData[fieldName]) {
            console.log(`Formatted initial date ${fieldName} from ${updatedData[fieldName]} to ${formattedDate}`);
            updatedData[fieldName] = formattedDate;
            hasChanges = true;
          }
        }
      });

      // Only update if we made changes
      if (hasChanges) {
        setBusinessData(updatedData);
      }
    }
  }, [loading, businessData]);
  
  // Function to get the specific error message for a field
  const getErrorMessageForField = (fieldName) => {
    const error = validationErrors.find(e => e.field === fieldName);
    return error ? error.message : '';
  };
  
  // Render field based on type
  const renderField = (field) => {
    const value = businessData[field.name] || '';
    const hasError = validationErrors.some(error => error.field === field.name);
    const isEmpty = value === undefined || value === null || 
      (typeof value !== 'number' && value.toString().trim() === '');
    // Mark as invalid if there's a validation error or if empty (all fields are required)
    const isInvalid = hasError || isEmpty;
    const validator = getValidatorForField(field);
    
    // Get any specific error message for this field
    const errorMessage = getErrorMessageForField(field.name);
    
    // Check for validation error message
    const validationErrorMessage = hasError 
      ? validationErrors.find(error => error.field === field.name)?.message
      : isEmpty ? `${field.label} is required` : '';
    
    // Check if this is a numerical field that should be rendered as a number input
    const isNumberField = field.name === 'RequestedAmount' || field.name === 'AverageBankBalance';
    
    // Check if this is an auto-populated field that should be read-only
    const isAutoPopulatedField = field.name === 'ContactTitle' || field.name === 'Contact2Title' || field.name === 'SICCode';
    
    // Render state dropdown for state fields - now handles both primary and secondary contact states
    if (isStateField(field.name)) {
      return (
        <>
          <Form.Select
            id={field.name}
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
            required={true}
            className="mt-0"
          >
            <option value="">Select a state</option>
            {US_STATES.map(state => (
              <option key={state.code} value={state.code}>
                {state.name} ({state.code})
              </option>
            ))}
          </Form.Select>
          {isInvalid && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {errorMessage || `Please select a state`}
            </div>
          )}
        </>
      );
    } else if (field.name === 'EntityType') {
      // Entity Type values
      const entityTypes = [
        { value: 'LLC', label: 'LLC' },
        { value: 'Corporation', label: 'Corporation' },
        { value: 'Partnership', label: 'Partnership' },
        { value: 'Sole Proprietorship', label: 'Sole Proprietorship' }
      ];
      
      // If current value is not in the list, default to LLC
      const currentValue = entityTypes.some(type => type.value === value) ? value : 'LLC';
      
      // Update business data if the value changed
      if (value && value !== currentValue) {
        // Use setTimeout to avoid React state update warning during render
        setTimeout(() => {
          handleInputChange(field.name, currentValue);
        }, 0);
      }
      
      return (
        <>
          <Form.Select
            id={field.name}
            value={currentValue}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
            required={true}
            className="mt-0"
          >
            <option value="">Select entity type</option>
            {entityTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </Form.Select>
          {isInvalid && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {errorMessage || `Please select an entity type`}
            </div>
          )}
        </>
      );
    } else if (field.type === 'date') {
      const dateValue = stringToDate(value);

      return (
        <>
          <div className="modern-date-wrapper">
            <DatePicker
              id={field.name}
              selected={dateValue}
              onChange={(date) => handleInputChange(field.name, dateToString(date))}
              dateFormat="MMM d, yyyy"
              placeholderText={`Select ${field.label}`}
              className={`form-control mt-0 ${isInvalid ? 'is-invalid' : ''}`}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              yearDropdownItemNumber={100}
              scrollableYearDropdown
              popperClassName="date-picker-popper"
              todayButton="Today"
              peekNextMonth
              showPopperArrow={false}
              customInput={
                <Form.Control 
                  isInvalid={isInvalid}
                  required={true}
                  aria-label={`Date field for ${field.label}`}
                />
              }
            />
          </div>
          {isInvalid && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {errorMessage || `${field.label} is required`}
            </div>
          )}
        </>
      );
    } else if (isNumberField) {
      // Render number inputs for RequestedAmount and AverageBankBalance
      return (
        <>
          <Form.Control
            id={field.name}
            type="number"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
            required={true}
            className="mt-0"
            min="0"
            step="0.01"
            onBlur={() => {
              if (isEmpty) {
                setShowValidationErrors(true);
                validateForm(); // Force validation on blur
              }
            }}
          />
          {isInvalid && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {errorMessage || `${field.label} is required`}
            </div>
          )}
        </>
      );
    } else if (isAutoPopulatedField) {
      return (
        <>
          <Form.Control
            id={field.name}
            type="text"
            value={value}
            readOnly={true}
            className="mt-0 bg-light"
          />
        </>
      );
    } else if (validator) {
      // Use the ValidatedInput component for fields with a validator
      return (
        <ValidatedInput
          id={field.name}
          name={field.name}
          value={value}
          onChange={handleInputChange}
          validator={validator}
          required={true}
          showValidation={true}
          validateOnChange={true}
          validateAfterLength={0} // Start validation immediately
          isInvalid={isInvalid}
          externalErrorMessage={errorMessage}
          className="mt-0"
        />
      );
    } else {
      // Regular text input for fields without a specific validator
      return (
        <>
          <Form.Control
            id={field.name}
            type="text"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            isInvalid={isInvalid}
            required={true}
            className="mt-0"
            onBlur={() => {
              if (isEmpty) {
                setShowValidationErrors(true);
                validateForm(); // Force validation on blur
              }
            }}
          />
          {isInvalid && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {errorMessage || `${field.label} is required`}
            </div>
          )}
        </>
      );
    }
  };
  
  // Wrap setActiveSection to create a smooth transition effect
  const changeSectionWithTransition = (newSectionIndex) => {
    if (sectionTransitioning) return; // Prevent multiple rapid section changes
    
    // Start transition
    setSectionTransitioning(true);
    
    // Add a small delay to allow the fade-out to occur before changing sections
    // This makes the transition smoother
    setTimeout(() => {
      setActiveSection(newSectionIndex);
      
      // Reset the transitioning state after a short delay to allow the fade-in to complete
      setTimeout(() => {
        setSectionTransitioning(false);
      }, 300);
    }, 50);
  };
  
  // Render the active section
  const renderActiveSection = () => {
    // Check if we're on the Lender Submission section
    const currentCategory = categories[activeSection];
    
    if (currentCategory === 'Submit to Lender') {
      return (
        <div className="content-fade-in">
          {renderLenderSubmissionSection()}
        </div>
      );
    }
    
    // Check if we're on the File Uploads section
    if (currentCategory === 'File Uploads') {
      return (
        <div className="section-content content-fade-in">
          <div className="section-header">
            <h4 className="section-title">File Uploads</h4>
          </div>
          
          <div className="section-description">
            Please upload your bank statements (required) and application documents (optional).
          </div>
          
          <FileUploads
            bankFiles={bankFiles}
            setBankFiles={setBankFiles}
            appFiles={appFiles}
            setAppFiles={setAppFiles}
            disabled={syncWithCRM}
            submitting={syncLoading}
            contactId={contactId}
            enableHubspotFiles={true}
            preloadedHubspotFiles={preloadedHubspotFiles}
            hubspotFilesLoading={hubspotFilesLoading}
          />
          
          <div className="navigation-buttons">
            <div>
              {activeSection > 0 && (
                <Button 
                  variant="outline-secondary" 
                  onClick={() => changeSectionWithTransition(activeSection - 1)}
                  size="lg"
                  className="px-4"
                >
                  Previous
                </Button>
              )}
            </div>
            
            <div>
              <Button 
                variant="primary" 
                onClick={() => changeSectionWithTransition(activeSection + 1)}
                size="lg"
                className="px-5 fw-bold"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      );
    }
    
    // Use the regular section rendering for all other sections
    const categoryFields = businessFields.filter(field => 
      getMappedCategory(field.category) === currentCategory
    );
    
    // Group fields by their original category for better organization
    const fieldsByOriginalCategory = {};
    categoryFields.forEach(field => {
      if (!fieldsByOriginalCategory[field.category]) {
        fieldsByOriginalCategory[field.category] = [];
      }
      fieldsByOriginalCategory[field.category].push(field);
    });
    
    // Add secondary contact toggle button only for Contact Information section
    const isContactSection = currentCategory === 'Contact Information';
    
    // Determine what to render for the secondary contact button based on settings
    const renderSecondaryContactButton = () => {
      // If HubSpot mode is enabled, show a different UI or disable the button
      if (isContactSection && secondaryContactHandling === 'hubspot') {
        return (
          <div className="ms-auto d-flex align-items-center">
            {hasSecondaryContact ? (
              <Button 
                variant="outline-danger"
                size="sm"
                onClick={toggleSecondaryContact}
              >
                <FaUserMinus className="me-1" /> Remove Secondary Contact
              </Button>
            ) : (
              <div className="d-flex align-items-center">
                <small className="text-muted me-2">
                  Auto-populated from HubSpot when available
                </small>
                <Button 
                  variant="outline-secondary"
                  size="sm"
                  onClick={toggleSecondaryContact}
                  title="Manually add a secondary contact"
                >
                  <FaUserPlus className="me-1" /> Add Manually
                </Button>
              </div>
            )}
          </div>
        );
      }
      
      // Default manual mode - show the regular add/remove button
      if (isContactSection) {
        return (
          <Button 
            variant={hasSecondaryContact ? "outline-danger" : "outline-primary"}
            size="sm"
            onClick={toggleSecondaryContact}
            className="ms-auto"
          >
            {hasSecondaryContact ? (
              <>
                <FaUserMinus className="me-1" /> Remove Secondary Contact
              </>
            ) : (
              <>
                <FaUserPlus className="me-1" /> Add Secondary Contact
              </>
            )}
          </Button>
        );
      }
      
      return null;
    };
    
    return (
      <div className="section-content content-fade-in">
        <div className="section-header d-flex justify-content-between align-items-center">
          <h4 className="section-title">{currentCategory}</h4>
          
          {renderSecondaryContactButton()}
        </div>
        
        <div className="section-description">
          Please fill out all required fields in this section.
        </div>
        
        <Form>
          {/* Render fields grouped by their original category */}
          {Object.entries(fieldsByOriginalCategory).map(([originalCategory, fields], groupIndex) => (
            <div key={originalCategory} className={originalCategory === 'Contact Address' && hasSecondaryContact ? 'mb-5' : ''}>
              {/* Only show subsection headers for primary contact */}
              {Object.keys(fieldsByOriginalCategory).length > 1 && (
                <h5 className="subsection-title mt-4 mb-3">
                  {originalCategory === 'Primary Contact' ? 'Primary Contact' : 
                   originalCategory === 'Contact Address' ? 'Primary Contact Address' : 
                   originalCategory}
                </h5>
              )}
              
              <Row>
                {fields.map((field) => (
                  <Col md={6} key={field.name} className="mb-3">
                    <Form.Group className="mb-0">
                      <Form.Label className="form-label" htmlFor={field.name}>
                        {field.label} <span className="required">*</span>
                      </Form.Label>
                      {renderField(field)}
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            </div>
          ))}

          {/* Render secondary contact fields if enabled */}
          {isContactSection && hasSecondaryContact && (
            <>
              <div className="secondary-contact-section mt-4 pt-4 border-top">
                <h5 className="subsection-title mb-3 d-flex align-items-center">
                  Secondary Contact
                  {secondaryContactHandling === 'hubspot' ? (
                    <Badge bg="info" className="ms-2 fs-6">From HubSpot</Badge>
                  ) : (
                    <Badge bg="secondary" className="ms-2 fs-6">Manual Entry</Badge>
                  )}
                </h5>
                <Row>
                  {businessFields.filter(field => field.category === 'Primary Contact').map((primaryField) => {
                    // Create the secondary contact field by changing the name
                    const secondaryField = {
                      ...primaryField,
                      name: `Contact2${primaryField.name.replace('Contact', '')}`,
                      // Update the label to show it's for secondary contact
                      label: primaryField.label
                    };
                    
                    // Determine if this specific field has an error
                    const fieldHasError = validationErrors.some(error => error.field === secondaryField.name);
                    const fieldErrorMessage = getErrorMessageForField(secondaryField.name);
                    
                    return (
                      <Col md={6} key={secondaryField.name} className="mb-3">
                        <Form.Group className="mb-0">
                          <Form.Label className="form-label" htmlFor={secondaryField.name}>
                            {secondaryField.label} <span className="required">*</span>
                          </Form.Label>
                          {renderField({
                            ...secondaryField,
                            // Pass error state and message explicitly for ValidatedInput
                            isInvalid: fieldHasError,
                            externalErrorMessage: fieldErrorMessage
                          })}
                        </Form.Group>
                      </Col>
                    );
                  })}
                </Row>
              </div>

              <div className="secondary-contact-address mt-4 pt-2">
                <h5 className="subsection-title mb-3">Secondary Contact Address</h5>
                <Row>
                  {businessFields.filter(field => field.category === 'Contact Address').map((primaryField) => {
                    // Create the secondary contact address field
                    const secondaryField = {
                      ...primaryField,
                      name: `Contact2${primaryField.name.replace('Contact', '')}`,
                      label: primaryField.label
                    };
                    
                     // Determine if this specific field has an error
                    const fieldHasError = validationErrors.some(error => error.field === secondaryField.name);
                    const fieldErrorMessage = getErrorMessageForField(secondaryField.name);

                    return (
                      <Col md={6} key={secondaryField.name} className="mb-3">
                        <Form.Group className="mb-0">
                          <Form.Label className="form-label" htmlFor={secondaryField.name}>
                            {secondaryField.label} <span className="required">*</span>
                          </Form.Label>
                           {renderField({
                            ...secondaryField,
                            // Pass error state and message explicitly for ValidatedInput
                            isInvalid: fieldHasError,
                            externalErrorMessage: fieldErrorMessage
                          })}
                        </Form.Group>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            </>
          )}
          
          <div className="navigation-buttons">
            <div>
              {activeSection > 0 && (
                <Button 
                  variant="outline-secondary" 
                  onClick={() => changeSectionWithTransition(activeSection - 1)}
                  size="lg"
                  className="px-4"
                >
                  Previous
                </Button>
              )}
            </div>
            
            <div>
              {/* For all sections including Contact Information, go to the next section in sequence */}
              {activeSection < categories.length - 1 && (
                <Button 
                  variant="primary" 
                  onClick={() => changeSectionWithTransition(activeSection + 1)}
                  size="lg"
                  className="px-5 fw-bold"
                >
                  Next
                </Button>
              )}
              
              {/* For the last regular section, go to Lender Submission */}
              {activeSection === categories.length - 1 && (
                <Button 
                  variant="primary" 
                  onClick={() => changeSectionWithTransition(categories.length)}
                  size="lg"
                  className="px-5 fw-bold"
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </Form>
      </div>
    );
  };
  
  // Modify the lender submission section
  const renderLenderSubmissionSection = () => {
    const hasErrors = validationErrors.length > 0;
    const filesRequired = !fileUploadsComplete;
    
    // Filter out File Uploads and Submit to Lender categories when checking form field completion
    const allFormFieldsCompleted = Object.entries(completionStats)
      .filter(([category]) => category !== 'File Uploads' && category !== 'Submit to Lender')
      .every(([_, stats]) => stats.completed === stats.total && stats.total > 0);
    
    return (
      <div className="section-content">
        <div className="section-header">
          <h4 className="section-title">Submit to Lender</h4>
        </div>
        
        {/* Lender Submission Component */}
        <div className="lender-submission-wrapper">
          {hasErrors && (
            <Alert variant="danger" className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="me-2">⚠️</span>
                  <span>Please fix all validation errors before submitting to lenders.</span>
                </div>
                <Button variant="outline-danger" size="sm" onClick={goToFirstError}>
                  Go to first error
                </Button>
              </div>
            </Alert>
          )}
          
          {filesRequired && (
            <Alert variant="warning" className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="me-2">⚠️</span>
                  <span>Please upload bank statements to proceed. Bank statements are required for submission.</span>
                </div>
                <Button 
                  variant="outline-warning" 
                  size="sm" 
                  onClick={() => {
                    // Find the index of File Uploads section
                    const fileUploadIndex = categories.findIndex(cat => cat === 'File Uploads');
                    changeSectionWithTransition(fileUploadIndex);
                  }}
                >
                  Go to File Uploads
                </Button>
              </div>
            </Alert>
          )}
          
          {!hasErrors && !filesRequired && !allFormFieldsCompleted && (
            <Alert variant="warning" className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="me-2">⚠️</span>
                  <span>Some required form fields are still incomplete. Please complete all fields before submitting.</span>
                </div>
                <Button 
                  variant="outline-warning" 
                  size="sm" 
                  onClick={() => {
                    // Find the first incomplete section
                    const incompleteIndex = categories.findIndex(category => {
                      const stats = completionStats[category] || { completed: 0, total: 0 };
                      return stats.completed !== stats.total;
                    });
                    if (incompleteIndex !== -1) {
                      changeSectionWithTransition(incompleteIndex);
                    }
                  }}
                >
                  Go to Incomplete Section
                </Button>
              </div>
            </Alert>
          )}

          <LenderSubmission 
            contactData={contactData} 
            opportunityData={businessData}
            bankFiles={bankFiles}
            appFiles={appFiles}
            disabled={hasErrors || filesRequired || !allFormFieldsCompleted || (syncWithCRM && (!syncStatus || syncStatus.type !== 'success'))}
            submissionHistory={submissionHistory} // Pass submission history to component
          />
          
          {/* Sync link at the bottom of the lender section */}
          <div className="mt-3 text-start">
            <Button
              size="sm"
              variant={!hasErrors ? (syncStatus?.type === 'success' ? "outline-success" : "outline-primary") : "outline-secondary"}
              className="compact-sync-btn"
              onClick={!hasErrors && !syncLoading ? handleSyncWithCRM : undefined}
              disabled={syncLoading || hasErrors}
            >
              {syncLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  <span>Syncing...</span>
                </>
              ) : (
                <>
                  {!hasErrors && syncStatus?.type === 'success' ? "✓ " : ""}
                  Sync with HubSpot
                </>
              )}
            </Button>
            
            {hasErrors && (
              <div className="text-danger mt-1 small">
                <em>
                  Fix all validation errors to enable sync
                </em>
              </div>
            )}
            
            {syncStatus && (
              <div className={`text-${syncStatus.type === 'error' ? 'danger' : syncStatus.type} mt-1 small`}>
                {syncStatus.message}
              </div>
            )}
          </div>
        </div>
        
        <div className="navigation-buttons">
          <div>
            <Button 
              variant="outline-secondary" 
              onClick={() => changeSectionWithTransition(activeSection - 1)}
              size="lg"
              className="px-4"
            >
              Previous
            </Button>
          </div>
          
          <div>
            {/* Empty div to maintain layout */}
          </div>
        </div>
      </div>
    );
  };
  
  // Calculate errors by category for navigation display
  const getErrorCountByCategory = () => {
    const errorCounts = {};
    
    validationErrors.forEach(error => {
      if (!errorCounts[error.category]) {
        errorCounts[error.category] = 0;
      }
      errorCounts[error.category]++;
    });
    
    return errorCounts;
  };
  
  // Auto-populate certain fields with default values
  useEffect(() => {
    if (!loading && Object.keys(businessData).length > 0) {
      const fieldsToAutoPopulate = {};
      let hasChanges = false;
      
      // Auto-populate primary owner title if empty
      if (!businessData.ContactTitle || businessData.ContactTitle.trim() === '') {
        fieldsToAutoPopulate.ContactTitle = "Owner";
        hasChanges = true;
        console.log("Auto-populated ContactTitle with 'Owner'");
      }
      
      // Auto-populate secondary owner title if secondary contact exists and title is empty
      if (hasSecondaryContact && (!businessData.Contact2Title || businessData.Contact2Title.trim() === '')) {
        fieldsToAutoPopulate.Contact2Title = "Owner";
        hasChanges = true;
        console.log("Auto-populated Contact2Title with 'Owner'");
      }
      
      // Always set SIC Code to 0000 regardless of current value
      if (businessData.SICCode !== "0000") {
        fieldsToAutoPopulate.SICCode = "0000";
        hasChanges = true;
        console.log("Enforced SICCode to 0000");
      }
      
      // Update businessData if we have changes
      if (hasChanges) {
        setBusinessData(prevData => ({
          ...prevData,
          ...fieldsToAutoPopulate
        }));
      }
    }
  }, [loading, hasSecondaryContact]); // Remove businessData from dependencies to prevent loops
  
  // Handle scrolling to the first error field
  const scrollToField = (fieldId) => {
    // First find which category the field belongs to
    const errorField = businessFields.find(field => field.name === fieldId);
    if (errorField) {
      const fieldCategory = getMappedCategory(errorField.category);
      const categoryIndex = categories.findIndex(category => category === fieldCategory);
      
      // Set the active section to the category containing the error
      if (categoryIndex !== -1) {
        changeSectionWithTransition(categoryIndex);
        
        // Use a short delay to ensure the DOM has updated before scrolling
        setTimeout(() => {
    const element = document.getElementById(fieldId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus();
      // Highlight the field
      element.classList.add('highlight-error');
      setTimeout(() => {
        element.classList.remove('highlight-error');
      }, 2000);
          }
        }, 100);
      }
    } else {
      console.warn(`Field with ID ${fieldId} not found in business fields`);
    }
  };
  
  // For backward compatibility - scrolls to first error
  const goToFirstError = () => {
    console.log("Go to first error called, validation errors:", validationErrors);
    
    if (validationErrors.length > 0) {
      const firstError = validationErrors[0];
      console.log("Navigating to first error:", firstError);
      
      // First validate that the field exists in businessFields
      const errorField = businessFields.find(field => field.name === firstError.field);
      
      if (errorField) {
        console.log("Found error field in business fields:", errorField);
        scrollToField(firstError.field);
      } else {
        console.error("Error field not found in business fields:", firstError.field);
        
        // As a fallback, try to navigate to the category
        if (firstError.category) {
          const categoryIndex = categories.findIndex(cat => cat === firstError.category);
          if (categoryIndex !== -1) {
            console.log("Navigating to error category:", firstError.category);
            changeSectionWithTransition(categoryIndex);
          }
        }
      }
    } else {
      console.warn("No validation errors found when goToFirstError was called");
    }
  };
  
  // Toggle secondary contact visibility
  const toggleSecondaryContact = () => {
    if (hasSecondaryContact) {
      // Show confirmation before removing secondary contact
      if (window.confirm("Are you sure you want to remove the secondary contact? All data will be lost.")) {
        // Remove all Contact2* fields from businessData
        const updatedData = { ...businessData };
        Object.keys(updatedData).forEach(key => {
          if (key.startsWith('Contact2')) {
            delete updatedData[key];
          }
        });
        setBusinessData(updatedData);
        setHasSecondaryContact(false);
        setAutoLoadedSecondaryContact(false);
        
        // Update validation
        validateForm();
        updateCompletionStats();
      }
    } else {
      // Always enable the secondary contact container when manually adding
      setHasSecondaryContact(true);
      
      // Auto-populate secondary contact title
      setBusinessData(prevData => ({
        ...prevData,
        Contact2Title: "Owner"
      }));
      
      // Check if we should try to fetch data from HubSpot
      if (secondaryContactHandling === 'hubspot' && secondaryContactProperty && contactData?.properties) {
        const propertyValue = contactData.properties[secondaryContactProperty];
        const propertyHasValue = propertyValue !== null && 
                                propertyValue !== undefined && 
                                String(propertyValue).trim() !== '';
        
        console.log(`Manual add: Checking property ${secondaryContactProperty}:`, propertyValue, `Has value: ${propertyHasValue}`);
        
        // Only try to auto-populate if the property actually has a value
        if (propertyHasValue) {
          console.log('Property has value, attempting to auto-populate secondary contact fields');
          
          const fetchSecondaryContactFromHubspot = async () => {
            try {
              // Get the field mappings
              const fieldMappings = await getFieldMappings();
              console.log('Retrieved field mappings:', fieldMappings);
              
              // Create secondary contact data object
              const secondaryData = {};
              let hasUpdatedAnyField = false;
              
              // Get all contact fields that should have secondary counterparts
              const contactFields = businessFields.filter(field => 
                field.category === 'Primary Contact' || field.category === 'Contact Address'
              );
              
              // Map each field using our stored mappings
              contactFields.forEach(primaryField => {
                const secondaryFieldName = `Contact2${primaryField.name.replace('Contact', '')}`;
                
                // Find the mapping for this field
                const mapping = fieldMappings.find(m => m.opportunityField === secondaryFieldName);
                
                if (mapping && mapping.hubspotField && contactData.properties[mapping.hubspotField] !== undefined) {
                  // Get the value and ensure it's a string
                  let value = contactData.properties[mapping.hubspotField];
                  
                  // Convert value to string based on its type
                  if (value === null || value === undefined) {
                    value = '';
                  } else if (typeof value === 'object') {
                    // If it's an object with a value property (common in HubSpot)
                    value = value.value ? String(value.value) : '';
                  } else {
                    value = String(value);
                  }
                  
                  // Special handling for date fields
                  if (primaryField.name.toLowerCase().includes('dateofbirth') || 
                      (primaryField.name.toLowerCase().includes('date') && 
                       primaryField.name.toLowerCase().includes('birth'))) {
                    value = formatDateToYYYYMMDD(value);
                  }
                  
                  // Special handling for numeric fields
                  if (primaryField.name.toLowerCase().includes('ownership') || 
                      primaryField.name.toLowerCase().includes('revenue') || 
                      primaryField.name.toLowerCase().includes('amount')) {
                    value = value.replace(/[^0-9.]/g, '');
                  }
                  
                  console.log(`Mapping ${secondaryFieldName} using ${mapping.hubspotField}:`, value);
                  secondaryData[secondaryFieldName] = value;
                  hasUpdatedAnyField = true;
                }
              });
              
              // Update the form if we found any fields to map
              if (hasUpdatedAnyField) {
                console.log('Applying secondary contact data:', secondaryData);
                setBusinessData(prevData => ({
                  ...prevData,
                  ...secondaryData
                }));
                setAutoLoadedSecondaryContact(true);
                success("Secondary contact information loaded from HubSpot", "Secondary Contact");
              } else {
                // No fields could be mapped, but we know the section is already enabled
                success("Secondary contact section added", "Secondary Contact");
              }
            } catch (error) {
              console.error('Error mapping secondary contact fields:', error);
              // Still keep the section enabled, just show an error
              notifyError("Failed to auto-populate secondary contact fields. Please fill in manually.", "Secondary Contact");
            }
          };
          
          fetchSecondaryContactFromHubspot();
        } else {
          // No usable secondary contact data in HubSpot, just show manual section
          console.log(`MANUAL OVERRIDE: Secondary contact section manually added despite empty property ${secondaryContactProperty}.`);
          warning("Adding secondary contact manually. No data found in HubSpot.", "Secondary Contact");
        }
      } else {
        // Manual mode, just show the section
        console.log('Manual secondary contact mode. Showing empty section.');
        success("Secondary contact section added", "Secondary Contact");
      }
    }
  };
  
  // Add CSS styles for secondary contact section
  useEffect(() => {
    // Add the CSS styles dynamically
    const style = document.createElement('style');
    style.textContent = `
      .secondary-contact-section, .secondary-contact-address {
        background-color: var(--light);
        border-radius: 0.5rem;
        padding: 1.5rem;
        margin-bottom: 1.5rem;
      }
      
      .secondary-contact-section h5, .secondary-contact-address h5 {
        color: var(--primary);
        font-weight: 600;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Fetch submission history when business data is available
  useEffect(() => {
    const fetchSubmissionHistory = async () => {
      // Only fetch if we have business data with a Federal Tax ID
      if (!loading && businessData?.FederalTaxID) {
        try {
          console.log(`Fetching submission history for Federal Tax ID: ${businessData.FederalTaxID}`);
          const history = await getSubmissionHistory(businessData.FederalTaxID);
          console.log('Submission history:', history);
          setSubmissionHistory(history);
        } catch (error) {
          console.error('Error fetching submission history:', error);
          // Don't show error to user as this is a non-critical feature
        }
      }
    };
    
    fetchSubmissionHistory();
  }, [businessData?.FederalTaxID, loading]);
  
  // Loading spinner
  if (loading) {
    return (
      <div className="single-spinner-container">
        <div className="loading-dots">
          <div className="dot"></div>
          <div className="dot"></div>
          <div className="dot"></div>
        </div>
        <p className="loading-text">Loading contact data...</p>
      </div>
    );
  }
  
  // Error message
  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </Container>
    );
  }
  
  // Main content
  return (
    <div className="contact-detail">
      {/* Loading indicator */}
      {loading && (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
          <div className="loading-dots">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      )}

      {!loading && (
        <div className={`contact-detail-container content-fade-in ${sectionTransitioning ? 'section-transitioning' : ''}`}>
          <Container fluid>
            <Row className="g-0">
              {/* Left Navigation */}
              <Col lg={3} xl={2} className="sidebar">
                <SideNavigation 
                  categories={categories}
                  activeSection={activeSection}
                  setActiveSection={changeSectionWithTransition}
                  completionStats={completionStats}
                  contactData={contactData}
                  errorCounts={getErrorCountByCategory()}
                  fileUploadStats={fileUploadStats}
                  fileUploadsComplete={fileUploadsComplete}
                  hasSecondaryContact={hasSecondaryContact}
                />
              </Col>
              
              {/* Main Content */}
              <Col lg={9} xl={10} className="main-content">
                <div className="staggered-fade-in">
                  {/* Auto-loaded Secondary Contact Alert */}
                  {autoLoadedSecondaryContact && (
                    <Alert variant="info" className="mb-3" dismissible onClose={() => setAutoLoadedSecondaryContact(false)}>
                      <div className="d-flex align-items-center">
                        <span className="me-2">ℹ️</span>
                        <span>Secondary contact information has been automatically loaded from HubSpot.</span>
                      </div>
                    </Alert>
                  )}
                  
                  {/* Sync Status */}
                  {syncStatus && (
                    <Alert variant={syncStatus.type === 'error' ? 'danger' : syncStatus.type} className="mb-3">
                      {syncStatus.message}
                    </Alert>
                  )}
                  
                  {/* Active Section Content */}
                  {renderActiveSection()}
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </div>
  );
};

export default ContactDetail; 
